import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FImage from 'components/BaseComponents/FImage'
import SBox from 'components/opinionless/SBox'
import SText from 'components/opinionless/SText'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { StepWizardChildProps } from 'react-step-wizard'
import { FormProvider, useForm } from 'react-hook-form'
import { SStyled, useSTheme } from 'styles/theme'
import { ga4_useTitle } from 'utils/ga4'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import style from './style.module.scss'
import { useMultipleDocumentsUploadMutation } from 'types/graphql'
import FFileUploadV2 from 'components/BaseComponents/FFileUploadV2'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import moment from 'moment-timezone'
import ConditionalRender from 'utils/conditionalRender'

const LBTextHeader = SStyled(SText)`
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`

interface Props {
    setCurrentStep?: (number) => void
    selectedMember?: any
    fontFamily: any
    nextStep?: any
    selectedDocument?: any
    onPrevious?: any
    updatePercentage: any
    onSuccess?: any
    disableBackButton?: boolean
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

const DocumentsProofOfHouseholdIncomeStep3: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const form = useForm()
    const { t } = useTranslation(window.LANG_NS)
    const applicationContext = useContext(ApplicationContext)
    const [docsFromServerForApplicant, setDocsFromServerForApplicant] = useState([])
    const [docsFromServerForMember, setDocsFromServerForMember] = useState([])
    const [isUploading, setIsUploading] = useState(false)
    const [documentCategory, setDocumentCategory] = useState(props.selectedDocument)
    const fontFamily = props.fontFamily
    const textStyles = {
        textAlignLast: 'left',
        whiteSpace: 'pre-line',
        fontFamily: fontFamily.body,
        lineHeight: '140%',
        color: theme.colors.gray400,
        fontSize: '14px',
        marginBottom: '5px',
        marginTop: '0px !important',
    }

    const [uploadDocMutation, { loading }] = useMultipleDocumentsUploadMutation()
    const { refetch } = useApplicationBasicInfo()
    const {
        loading: applicationAnswersLoading,
        refetch: refetchApplicationAnswers,
        getAnswersByCategory,
    } = useApplicationAnswers()
    const onUpload = async (file, householdMemberId) => {
        const documentType = 'PROOF_OF_INCOME'

        setIsUploading(true)

        if (file) {
            await uploadDocMutation({
                variables: {
                    files: [file],
                    personId: applicationContext?.profile?.person?.id,
                    documentType: documentType,
                    householdMemberId: householdMemberId || null,
                },
            })
                .then(() => {
                    refetch()
                })
                .finally(() => {
                    setIsUploading(false)
                })
        }
    }
    const areHouseholdMembersDisplayed = useMemo(() => {
        return documentCategory === 'long_beach_pohi_one_paystub_width_3_months_unstyled'
    }, [documentCategory])

    const noUsersOrHouseholdMembersWithIncome = useMemo(() => {
        if (applicationContext.profile.earnedIncome === '0' && applicationContext.profile.unearnedIncome === '0') {
            const hasMembersWithIncome = !!applicationContext.householdMembers.filter(
                (member) => member.earnedIncome != '0' || member.unearnedIncome !== '0'
            ).length
            return !hasMembersWithIncome
        }

        return false
    }, [applicationContext])

    const allMembersHaveDocs =
        applicationContext.householdMembers?.length > 0 && areHouseholdMembersDisplayed
            ? applicationContext.householdMembers
                  .filter((member) => member.earnedIncome !== '0')
                  .every((householdMember) => {
                      const memberDocs = applicationContext.documents.filter(
                          (doc) =>
                              doc.node.person.id === applicationContext.profile.person.id &&
                              doc.node.householdMember?.id === householdMember.id &&
                              doc.node.name &&
                              doc.node.documentType === 'PROOF_OF_INCOME'
                      )
                      return memberDocs.length > 0
                  })
            : true

    const applicantHasDocs = useMemo(() => {
        if (applicationContext.profile.earnedIncome !== '0') {
            return applicationContext.documents.find(
                (itm) =>
                    itm.node.person.id === applicationContext.profile.person.id &&
                    itm.node.documentType === 'PROOF_OF_INCOME' &&
                    itm.node.name &&
                    !itm.node.householdMember?.id
            )
        } else {
            if (areHouseholdMembersDisplayed) {
                return true
            } else {
                return applicationContext.documents.find(
                    (itm) =>
                        itm.node.person.id === applicationContext.profile.person.id &&
                        itm.node.documentType === 'PROOF_OF_INCOME' &&
                        itm.node.name &&
                        !itm.node.householdMember?.id
                )
            }
        }
    }, [applicationContext])

    useEffect(() => {
        if (applicationContext?.documents?.length) {
            const selectedMemberDocuments = areHouseholdMembersDisplayed
                ? applicationContext.documents
                      .filter(
                          (itm) =>
                              itm.node.person.id === applicationContext.profile.person.id &&
                              itm.node.documentType === 'PROOF_OF_INCOME' &&
                              itm.node?.householdMember &&
                              itm.node.name
                      )
                      .map((doc) => doc.node)
                : []

            const documentForCurrentUser = applicationContext.documents
                .filter(
                    (itm) =>
                        itm.node.person.id === applicationContext.profile.person.id &&
                        !itm.node?.householdMember &&
                        itm.node.documentType === 'PROOF_OF_INCOME' &&
                        itm.node.name
                )
                .map((doc) => doc.node)
            setDocsFromServerForApplicant(documentForCurrentUser)
            setDocsFromServerForMember(selectedMemberDocuments)
        }
    }, [applicationContext?.documents])

    const onSubmit = async (e) => {
        e.preventDefault()
        props.onSuccess()
    }

    ga4_useTitle('Household income')
    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.documents,
        WIZARD_STEPS_MAPPING.documents.householdIncomeUploadTwo,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    const proofOfIncomeDocumentAnswer = t(`${getAnswersByCategory('PROOF_OF_INCOME_CATEGORY')?.[0]?.response}_unstyled`)

    return (
        <FormProvider {...form}>
            <Container>
                <SBox as="form" onSubmit={onSubmit}>
                    <ConditionalRender if={!props.disableBackButton}>
                        <FButtonPrevious onClick={props.onPrevious} />
                    </ConditionalRender>
                    <LBTextHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '32px',
                            color: theme.colors.secondaryColor,
                            fontFamily: fontFamily.header,
                        }}
                    >
                        {t('long_beach_documents_pohi_header')}
                    </LBTextHeader>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: theme.colors.black,
                            fontSize: '14px',
                            width: '150px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_documents_poa_upload_description'),
                        }}
                    />
                    <LBText
                        sx={{
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            color: theme.colors.gray400,
                            fontSize: '14px',
                            border: '1.623px solid #CCC',
                            background: '#F2F2F2',
                            padding: '13px',
                            marginTop: '2px !important',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t(documentCategory) || proofOfIncomeDocumentAnswer,
                        }}
                    />
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            lineHeight: '140%',
                            color: theme.colors.gray400,
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_documents_poa_upload_warning_description'),
                        }}
                    />
                    <div className={style.income_earners_container}>
                        <ConditionalRender
                            if={
                                areHouseholdMembersDisplayed &&
                                (applicationContext.profile.earnedIncome !== '0' ||
                                    applicationContext.profile.unearnedIncome !== '0')
                            }
                        >
                            <div className={style.household_applicant_blind_disabled_container}>
                                <div>
                                    <div
                                        className={style.household_applicant_blind_disabled_container__icon}
                                        style={{ display: 'flex', marginTop: '24px' }}
                                    >
                                        <FImage
                                            width={'50px'}
                                            height={'50px'}
                                            imageUrl={'/lb_household_income_person.svg'}
                                            alt="person"
                                        />
                                        <div style={{ marginLeft: '20px' }}>
                                            <LBText
                                                sx={{
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    fontFamily: fontFamily.body,
                                                    fontWeight: '700',
                                                    lineHeight: '140%',
                                                    color: theme.colors.lbBlue,
                                                    fontSize: '14px',
                                                    marginBottom: '5px',
                                                    marginTop: '0 !important',
                                                    textTransform: 'uppercase',
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: t('long_beach_applicant_name_label_relationship', {
                                                        name: `${applicationContext.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                                                        relationship: t('long_beach_household_applicant_label'),
                                                    }),
                                                }}
                                            />
                                            <LBText
                                                sx={{
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    fontFamily: fontFamily.body,
                                                    lineHeight: '140%',
                                                    color: theme.colors.gray400,
                                                    fontSize: '14px',
                                                    marginBottom: '5px',
                                                    marginTop: '0px !important',
                                                }}
                                            >
                                                Date of Birth:{' '}
                                                {moment(applicationContext.profile.dob).format('MM/DD/YYYY')} (Age{' '}
                                                {moment().diff(applicationContext.profile.dob, 'years')})
                                            </LBText>
                                            <LBText
                                                sx={{
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    fontFamily: fontFamily.body,
                                                    lineHeight: '140%',
                                                    color: theme.colors.gray400,
                                                    fontSize: '14px',
                                                    marginBottom: '5px',
                                                    marginTop: '0px !important',
                                                }}
                                            >
                                                {!applicationContext.profile.blindDisabed
                                                    ? t('ssi_benefits_wizard_blind_disabled_no')
                                                    : t('ssi_benefits_wizard_blind_disabled_yes')}{' '}
                                            </LBText>
                                            <LBText
                                                sx={{
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    fontFamily: fontFamily.body,
                                                    lineHeight: '140%',
                                                    color: theme.colors.gray400,
                                                    fontSize: '14px',
                                                    marginBottom: '5px',
                                                    marginTop: '0px !important',
                                                }}
                                            >
                                                {applicationContext.profile.earnedIncome
                                                    ? 'Income Earner'
                                                    : 'Not Income Earner'}
                                            </LBText>
                                            <LBText
                                                sx={{
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    fontFamily: fontFamily.body,
                                                    lineHeight: '140%',
                                                    color: theme.colors.gray400,
                                                    fontSize: '14px',
                                                    marginBottom: '5px',
                                                    marginTop: '0px !important',
                                                }}
                                            >
                                                Earned Income: ${applicationContext.profile.earnedIncome || '0'}
                                            </LBText>
                                            <LBText
                                                sx={{
                                                    textAlignLast: 'left',
                                                    whiteSpace: 'pre-line',
                                                    fontFamily: fontFamily.body,
                                                    lineHeight: '140%',
                                                    color: theme.colors.gray400,
                                                    fontSize: '14px',
                                                    marginBottom: '5px',
                                                    marginTop: '0px !important',
                                                }}
                                            >
                                                Unearned Income: ${applicationContext.profile.unearnedIncome || '0'}
                                            </LBText>
                                            <FFileUploadV2
                                                onUpload={(file) => {
                                                    onUpload(file, null)
                                                }}
                                                ref={form.register({ required: 'required_field' as string })}
                                                name={'proof_of_income_upload_file_1'}
                                                id={'proof_of_income_upload_file_1'}
                                                defaultDocument={
                                                    docsFromServerForApplicant?.length
                                                        ? docsFromServerForApplicant
                                                        : null
                                                }
                                                isUploading={isUploading}
                                                reverseDocuments
                                                multiple={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className={style.household_applicant_blind_disabled_container__applicant_group}
                                ></div>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={areHouseholdMembersDisplayed}>
                            {applicationContext.householdMembers.map((itm, key) => {
                                if (!parseInt(itm.earnedIncome, 10) && !parseInt(itm.unearnedIncome, 10)) {
                                    return null
                                }

                                const docsToRender = docsFromServerForMember.find(
                                    (item) => item.householdMember?.id === itm.id
                                )

                                return (
                                    <div key={key}>
                                        <div style={{ display: 'flex', marginTop: '14px' }}>
                                            <FImage
                                                imageUrl={'/lb_household_income_person_bw.svg'}
                                                alt="person_bw"
                                                width={'50px'}
                                                height={'50px'}
                                            />
                                            <div style={{ marginLeft: '20px' }}>
                                                <LBText
                                                    sx={{
                                                        textAlignLast: 'left',
                                                        whiteSpace: 'pre-line',
                                                        fontFamily: fontFamily.body,
                                                        fontWeight: '700',
                                                        lineHeight: '140%',
                                                        color: theme.colors.gray400,
                                                        fontSize: '14px',
                                                        marginBottom: '5px',
                                                        marginTop: '0 !important',
                                                        textTransform: 'uppercase',
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: t('long_beach_applicant_name_label_relationship', {
                                                            name: `${itm?.name}`.toUpperCase(),
                                                            relationship: t(`${itm?.relation}`),
                                                        }),
                                                    }}
                                                />

                                                <LBText sx={textStyles}>
                                                    Date of Birth: {moment(itm.dob, 'YYYY-MM-DD').format('MM/DD/YYYY')}{' '}
                                                    (Age:{' '}
                                                    {moment().diff(
                                                        moment(itm.dob, 'YYYY-MM-DD').format('MM/DD/YYYY'),
                                                        'years'
                                                    )}
                                                    )
                                                </LBText>
                                                <LBText sx={textStyles}>
                                                    {!itm.blindDisabed
                                                        ? t('ssi_benefits_wizard_blind_disabled_no')
                                                        : t('ssi_benefits_wizard_blind_disabled_yes')}
                                                </LBText>
                                                <LBText sx={textStyles}>
                                                    {itm.earnedIncome ? 'Income Earner' : 'Not Income Earner'}
                                                </LBText>

                                                <LBText sx={textStyles}>
                                                    Earned Income: ${itm.earnedIncome || '0'}
                                                </LBText>
                                                <LBText sx={textStyles}>
                                                    Unearned Income: ${itm.unearnedIncome || '0'}
                                                </LBText>
                                                <FFileUploadV2
                                                    onUpload={(file) => {
                                                        onUpload(file, itm.id)
                                                    }}
                                                    isUploading={isUploading}
                                                    ref={form.register({ required: 'required_field' as string })}
                                                    name={`proof_of_income_upload_file_${key + 3}`}
                                                    id={`proof_of_income_upload_file_${key + 3}`}
                                                    defaultDocument={docsToRender ? [docsToRender] : null}
                                                    multiple={false}
                                                />
                                            </div>
                                            <div
                                                className={
                                                    style.household_applicant_blind_disabled_container__applicant_group
                                                }
                                            ></div>
                                        </div>
                                    </div>
                                )
                            })}
                        </ConditionalRender>
                        <ConditionalRender if={!areHouseholdMembersDisplayed || noUsersOrHouseholdMembersWithIncome}>
                            <FFileUploadV2
                                onUpload={(file) => {
                                    onUpload(file, null)
                                }}
                                ref={form.register({ required: 'required_field' as string })}
                                name={'proof_of_income_upload_file_3'}
                                id={'proof_of_income_upload_file_3'}
                                defaultDocument={docsFromServerForApplicant?.length ? docsFromServerForApplicant : null}
                                multiple={false}
                            />
                        </ConditionalRender>
                    </div>
                    <LBText
                        sx={{
                            textAlignLast: 'left',
                            whiteSpace: 'pre-line',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                            marginTop: '0 !important',
                            lineHeight: '140%',
                            color: theme.colors.gray400,
                            fontSize: '14px',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: t('long_beach_document_pohi_upload_footer'),
                        }}
                    />
                    {allMembersHaveDocs &&
                        applicantHasDocs &&
                        (docsFromServerForApplicant?.length > 0 || docsFromServerForMember?.length > 0) && (
                            <LBButton
                                type={'submit'}
                                id={'continueButton'}
                                title={t('save_and_continue')}
                                width={'100%'}
                                loading={loading}
                                backgroundColor={loading && 'grey'}
                                mt={7}
                                mb={5}
                                textStyle={{
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    lineHeight: '140%',
                                    fontFamily: fontFamily.body,
                                    color: theme.colors.white,
                                }}
                            >
                                {t('save_and_continue')}
                            </LBButton>
                        )}
                </SBox>
            </Container>
        </FormProvider>
    )
}

export default DocumentsProofOfHouseholdIncomeStep3
